import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type { Location } from "react-router-dom";
import Modal from "../components/Modal";
import Spinner from "../components/Spinner";
import MinistryIcon from "../components/MinistryIcon";
import { useMass } from "../lib/useMass";
import { formatMassDateLabel, formatMassTimeParts } from "../lib/mass-time";
import { payloadTimeZone } from "../lib/timezone";
import { roleLabel } from "../lib/role-label";
import type { MassAssignment, MassDetail, MassGap } from "../types/mass";

export default function MassModal() {
  const { massId } = useParams();
  const navigate = useNavigate();
  const { data, error, loading } = useMass(massId);

  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  return (
    <Modal open onClose={close} ariaLabel="Mass detail">
      {loading ? (
        <Spinner label="Loading Mass" />
      ) : error || !data ? (
        <p className="placeholder">Couldn't load this Mass{error ? `: ${error}` : ""}.</p>
      ) : (
        <MassBody mass={data.mass} timeZone={payloadTimeZone(data.timezone)} />
      )}
    </Modal>
  );
}

function MassBody({ mass, timeZone }: { mass: MassDetail; timeZone: string }) {
  const location = useLocation();
  // Preserve the dashboard as the background so a sub-request row can
  // drill into the gap detail over the same backdrop.
  const backgroundLocation =
    (location.state as { backgroundLocation?: Location } | null)?.backgroundLocation ?? null;

  const massDate = new Date(mass.startsAt);
  const { time, suffix } = formatMassTimeParts(massDate, timeZone);
  const dateLabel = formatMassDateLabel(massDate, timeZone);

  return (
    <>
      <header className="gap-header">
        <p className="gap-header__kicker">Mass</p>
        <div className="gap-header__primary">
          <p className="gap-header__date">{dateLabel}</p>
          <h1 className="gap-header__time">
            <span className="gap-header__time-num">{time}</span>
            <span className="gap-header__time-suffix">{suffix}</span>
          </h1>
        </div>
      </header>

      {mass.gaps.length > 0 ? (
        <section className="mass-section" aria-labelledby="mass-gaps-title">
          <h2 id="mass-gaps-title" className="mass-section__title">
            Needs coverage
            <span className="mass-section__count">{mass.gaps.length}</span>
          </h2>
          <ul className="mass-gaps__list">
            {mass.gaps.map((g, i) => (
              <MassGapRow key={i} gap={g} backgroundLocation={backgroundLocation} />
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mass-section" aria-labelledby="mass-serving-title">
        <h2 id="mass-serving-title" className="mass-section__title">
          Serving
        </h2>
        <ul className="mass-roster__list">
          {mass.assignments.length === 0 ? (
            <li className="mass-roster__empty">No volunteers scheduled.</li>
          ) : (
            mass.assignments.map((a) => (
              <MassRosterRow key={`${a.ministry}-${a.volunteerId}`} a={a} />
            ))
          )}
        </ul>
      </section>
    </>
  );
}

function MassGapRow({
  gap,
  backgroundLocation,
}: {
  gap: MassGap;
  backgroundLocation: Location | null;
}) {
  const role = roleLabel(gap.ministry);
  const roleEl = (
    <span className="mass-gaps__role">
      <MinistryIcon ministry={role} />
      <span>{role}</span>
    </span>
  );

  // Sub-requests drill into the existing gap detail (draft email,
  // volunteers). Unfilled slots have no sub-request to open — they're
  // filled from the Volunteers page — so they render as a plain row.
  if (gap.kind === "sub_request") {
    return (
      <li className="mass-gaps__row">
        <Link
          className="mass-gaps__link"
          to={`/gap/${gap.subRequestId}`}
          state={{ backgroundLocation }}
        >
          {roleEl}
          <span className="mass-gaps__sep" aria-hidden="true">
            ·
          </span>
          <span className="mass-gaps__name">{gap.requesterName}</span>
          <span className="mass-gaps__chevron" aria-hidden="true">
            ›
          </span>
        </Link>
      </li>
    );
  }

  return (
    <li className="mass-gaps__row mass-gaps__row--unfilled">
      {roleEl}
      <span className="mass-gaps__unfilled">— unfilled</span>
    </li>
  );
}

function MassRosterRow({ a }: { a: MassAssignment }) {
  const role = roleLabel(a.ministry);
  return (
    <li className="mass-roster__row">
      <span className="mass-roster__role">
        <MinistryIcon ministry={role} />
        <span>{a.position && a.position !== a.ministry ? `${role} — ${a.position}` : role}</span>
      </span>
      <span className="mass-roster__sep" aria-hidden="true">
        ·
      </span>
      <span className="mass-roster__name">{a.name}</span>
    </li>
  );
}
